// @flow
import { createSelector } from 'reselect';
import { omit, omitBy } from 'lodash';

import { getDefaultProposal } from 'Utils/proposal/proposalUtils';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
import { tripInformationSelector } from 'Store/trip-information/storeHelper';
import { apiDateToTimestamp } from 'Utils/date/apiDate';

import { type CustomerSearchResultState } from './customer-search/customerSearch.reducer';

const bookingSelector = ({ booking }) => booking || {};
const additionalTransportsSelector = createSelector(
  bookingSelector,
  ({ additionalTransports }) => additionalTransports || {},
);
const getPaymentProviders = createSelector(
  bookingSelector,
  ({ paymentProviders }) => paymentProviders,
);

const getAdditionalTransports = createSelector(
  additionalTransportsSelector,
  ({ transports }) => transports,
);
const getAdditionalTransportsFilters = createSelector(
  additionalTransportsSelector,
  ({ filters }) => filters,
);
const getFilteredAdditionalTransports = createSelector(
  additionalTransportsSelector,
  ({ filteredTransports }) => filteredTransports,
);
const getAdditionalTransportsSorters = createSelector(
  additionalTransportsSelector,
  ({ sorters }) => sorters || [],
);
const getAdditionalTransportsError = createSelector(
  additionalTransportsSelector,
  ({ error }) => error,
);
const getIsLoadingAdditionalTransports = createSelector(
  additionalTransportsSelector,
  ({ isLoading }) => isLoading,
);

const additionalAccommodationsSelector = createSelector(
  bookingSelector,
  ({ additionalAccommodations }) => additionalAccommodations || {},
);
const getIsLoadingAdditionalAccommodations = createSelector(
  additionalAccommodationsSelector,
  ({ isLoading }) => isLoading,
);
const getAdditionalAccommodations = createSelector(
  additionalAccommodationsSelector,
  ({ accommodationsSets }) => accommodationsSets,
);
const getAdditionalAccommodationsFilters = createSelector(
  additionalAccommodationsSelector,
  ({ filters }) => filters,
);
const getAdditionalAccommodationsError = createSelector(
  additionalAccommodationsSelector,
  ({ error }) => error,
);
const getFilteredAdditionalAccommodations = createSelector(
  additionalAccommodationsSelector,
  ({ filteredAccommodations }) => filteredAccommodations,
);
const getFilteredAdditionalAccommodationsSets = createSelector(
  getFilteredAdditionalAccommodations,
  getAdditionalAccommodations,
  (filteredAccommodations, additionalAccommodations) => {
    return filteredAccommodations && filteredAccommodations.length > 0
      ? filteredAccommodations
      : additionalAccommodations;
  },
);

const proposalsSelector = createSelector(bookingSelector, ({ proposals }) => proposals || []);

const getCurrentResort = createSelector(bookingSelector, ({ resort }) => resort);

const findAttendeeByNeolid = (attendees, neolid) =>
  attendees.find(attendee => attendee.neolid === neolid);
const getSelectedProposalId = createSelector(
  bookingSelector,
  ({ selectedProposalId }) => selectedProposalId,
);

/**
 * It should be getSelectedProposal but there is a selector getSelectedProposal
 * witch get the proposal from paymentInfoSelector
 * I do not know why...
 */
const getCurrentProposal = createSelector(
  bookingSelector,
  ({ proposals, selectedProposalId }) =>
    /**
     * return the selected proposal if found,
     * if not (for any reason) the default proposal for keeping the return shape value consistent
     */
    proposals.find(proposal => proposal.id === selectedProposalId) || getDefaultProposal(proposals),
);

const filterProposalBySamePackage = (
  proposals: Array<Proposal>,
  selectedProposal: Proposal,
): Array<Proposal> => {
  const errorsProposals = proposals.filter(proposal => proposal.errorCode);

  if (errorsProposals.length) {
    return errorsProposals;
  }

  return proposals.filter(item => {
    const { accommodations = [], packageId } = item;

    return accommodations.length && packageId === selectedProposal.packageId;
  });
};

const getProposals = createSelector(
  proposalsSelector,
  getCurrentProposal,
  filterProposalBySamePackage,
);

const getProposalsWithMappedAttendees = createSelector(
  getProposals,
  tripInformationSelector,
  (proposals, tripInformation) => {
    if (!proposals) {
      return proposals;
    }

    return proposals.map(proposal => {
      return {
        ...proposal,
        attendees: {
          ...proposal.attendees,
          list: proposal.attendees.list.map(attendee => {
            // if there is a birthdate it's a children otherwise it's an adult
            const attendeeData = attendee.birthdate
              ? findAttendeeByNeolid(tripInformation.kids, attendee.customerId)
              : findAttendeeByNeolid(tripInformation.adults, attendee.customerId);

            if (!attendeeData) {
              return attendee;
            }

            return {
              ...attendee,
              firstName: attendeeData.firstName,
              lastName: attendeeData.lastName,
            };
          }),
        },
      };
    });
  },
);

const getProposalHasAnyEquipmentForRent = createSelector(
  bookingSelector,
  ({ rentalEquipments }) => rentalEquipments.hasAnyEquipmentForRent,
);

const getAttendees = createSelector(getCurrentProposal, ({ attendees }) => attendees);
const getConnectedUserInfoSelector = createSelector(
  userInfoSelector,
  connectedUserInfo =>
    connectedUserInfo.isQuickLoggedIn
      ? {}
      : omit(
          {
            ...connectedUserInfo,
            birthdate: connectedUserInfo.dateOfBirth,
            neolid: connectedUserInfo.neolaneId, // harmonize the neolid between user and seller
            mobile: connectedUserInfo.mobilePhoneNumber,
          },
          [
            'dateOfBirth',
            'GMNumber',
            'homePhoneNumber',
            'isProspect',
            'neolaneId',
            'unicodeCivility',
            'expirationDate',
            'mobilePhoneNumber',
          ],
        ),
);

const getSuggestions = createSelector(bookingSelector, ({ suggestions }) => suggestions);

export type AdultAttendeeWithInfosType = $ReadOnly<{
  id: string,
  neolid?: string,
  civility?: string,
  firstName?: string,
  lastName?: string,
  unicodeFirstName?: string,
  unicodeLastName?: string,
  email?: string,
  mobile?: string,
  birthdate?: number,
  passport?: $ReadOnly<{
    expirationDate?: string,
    documentNumber?: string,
    issuingDate?: string,
    nationality?: string,
  }>,
  identityNumber?: string,
  gmNumber: ?string,
  address?: $ReadOnly<{
    additionalInformation1?: string,
    additionalInformation2?: string,
    addOn?: string,
    city?: string,
    country?: string,
    countryCode?: string,
    stateOrDistrict?: string,
    streetName?: string,
    streetNumber?: string,
    town?: string,
    zipCode?: string,
  }>,
  loyaltyProgram?: {
    status: ?string,
    points: ?number,
  },
  optinEmail: ?boolean,
  optinSms: ?boolean,
  optinMail: ?boolean,
  optinFosun: ?boolean,
  optinPhone: ?boolean,
  optinClubmedPlus: ?boolean,
}>;

export type ChildAttendeeWithInfosType = $ReadOnly<{
  id: string,
  neolid?: string,
  civility?: string,
  firstName?: string,
  lastName?: string,
  unicodeFirstName?: string,
  unicodeLastName?: string,
  birthdate: number,
  gmNumber: ?string,
  passport?: $ReadOnly<{
    expirationDate?: string,
    documentNumber?: string,
    issuingDate?: string,
    nationality?: string,
  }>,
}>;

export type AttendeesWithInfosType = {
  adults: $ReadOnlyArray<AdultAttendeeWithInfosType>,
  // $FlowFixMe temporarily disabled: waiting for clic evolv
  children: $ReadOnlyArray<ChildAttendeeWithInfosType>,
};

/**
 * The comparator does not put proposal in store
 * We need to use this function instead of the getAttendeesWithInfos selector
 */
export const addUserInfosToAttendees = (
  proposalAttendees: ProposalAttendees,
  tripAttendees: *,
  connectedUserInfo: *,
): AttendeesWithInfosType => {
  const getSortAttendeesByGmNumber = (attendee1, attendee2) => {
    if (!attendee1.gmNumber || !attendee2.gmNumber) {
      return 0;
    }

    return (
      tripAttendees.adults.findIndex(attendee => attendee.gmNumber === attendee1.gmNumber) -
      tripAttendees.adults.findIndex(attendee => attendee.gmNumber === attendee2.gmNumber)
    );
  };
  // defensive code if store is not available (ex: confirmation page)
  if (!tripAttendees || !proposalAttendees) {
    return {
      adults: [],
      children: [],
    };
  }

  const sortedAdultsAttendees = tripAttendees.adults.sort(getSortAttendeesByGmNumber);

  const adultsList = proposalAttendees.adults.map((adult, index) => ({
    ...(index === 0 ? connectedUserInfo : null),
    ...adult,
    // tripAttendees is populate with undefined values we don't want to erase for nothing
    ...omitBy(sortedAdultsAttendees[index], value => value === undefined),
  }));

  const childrenList = proposalAttendees.children
    .map(child => ({
      ...child,
      // tripAttendees is populate with undefined values we don't want to erase for nothing
      // we need to keep the children order defined in the proposal by the api
      ...omitBy(
        tripAttendees.kids.find(kid => {
          if (kid.id) {
            return kid.birthdate === apiDateToTimestamp(child.birthdate) && kid.id === child.id;
          }

          if (kid.neolid && child.customerId) {
            return (
              kid.birthdate === apiDateToTimestamp(child.birthdate) &&
              kid.neolid === child.customerId
            );
          }

          return kid.birthdate === apiDateToTimestamp(child.birthdate);
        }),
        (value, key: string) => value === undefined || key === 'birthdate',
      ),
    }))
    .sort(getSortAttendeesByGmNumber)
    .sort((child1, child2) => {
      // for seller set children with neolid (so with his fields already filled) first
      if (child1.neolid && !child2.neolid) {
        return -1;
      }

      if (!child1.neolid && child2.neolid) {
        return 1;
      }

      return 0;
    });

  return {
    adults: adultsList,
    children: childrenList,
  };
};

export const getCustomerSearchResult = createSelector(
  bookingSelector,
  booking => booking.customerSearch,
);

const addSearchDataToAttendees = (
  attendeesWithInfo: AttendeesWithInfosType,
  attendeesFromSearch: AttendeesWithInfosType,
): AttendeesWithInfosType => {
  const enrichedAdults = attendeesWithInfo.adults.map((attendee, index) => {
    const attendeeFromSearch = attendeesFromSearch.adults[index];
    return {
      ...attendee,
      ...(attendeeFromSearch
        ? {
            ...attendeeFromSearch,
            /**
             * attendeeFromSearch has a "fake" id which is in reality customerId (see commonProfileFieldsFragment)
             * we want to have the proposal attendee id here because it's what the api is waiting for
             *  */
            id: attendee.id,
            customerId: attendeeFromSearch.id,
          }
        : null),
    };
  });

  const enrichedChildren = attendeesWithInfo.children.map((attendee, index) => {
    const attendeeFromSearch = attendeesFromSearch.children[index];
    return {
      ...attendee,
      ...(attendeeFromSearch
        ? {
            ...attendeeFromSearch,
            /**
             * attendeeFromSearch has a "fake" id which is in reality customerId (see commonProfileFieldsFragment)
             * we want to have the proposal attendee id here because it's what the api is waiting for
             *  */
            id: attendee.id,
            customerId: attendeeFromSearch.id,
          }
        : null),
    };
  });

  return {
    adults: enrichedAdults,
    children: enrichedChildren,
  };
};

const enrichAttendeesWithAvailableData = (
  proposalAttendees: ProposalAttendees,
  tripAttendees: *,
  connectedUserInfo: *,
  searchResult: CustomerSearchResultState,
): AttendeesWithInfosType => {
  const attendeesWithInfo = addUserInfosToAttendees(
    proposalAttendees,
    tripAttendees,
    connectedUserInfo,
  );

  return searchResult && searchResult.data
    ? addSearchDataToAttendees(attendeesWithInfo, searchResult.data)
    : attendeesWithInfo;
};

const getAttendeesWithInfos: (*) => AttendeesWithInfosType = createSelector(
  getAttendees,
  tripInformationSelector,
  getConnectedUserInfoSelector,
  getCustomerSearchResult,
  enrichAttendeesWithAvailableData,
);

const getReservedServicesByAttendee = createSelector(
  getCurrentProposal,
  getAttendeesWithInfos,
  ({ additionalServices }, attendeesWithInfos): $ReadOnlyArray<ReservedServicesByAttendee> => {
    // defensive code if store is not available (ex: confirmation page)
    if (!additionalServices || !attendeesWithInfos) {
      return [];
    }

    const attendeesWithServiceRented = [
      ...additionalServices.activities,
      ...additionalServices.rentals,
    ].reduce((acc, service) => {
      const { pricingInfo, productInfo } = service;

      pricingInfo.schedules.forEach(({ attendees }) => {
        attendees.forEach(attendee => {
          if (attendee.isRented) {
            if (acc[attendee.id]) {
              if (acc[attendee.id][productInfo.id]) {
                acc[attendee.id][productInfo.id].price += attendee.price.value;
                if (
                  !acc[attendee.id][productInfo.id].services.some(
                    ({ pricingInfo: servicePricingInfo }) =>
                      servicePricingInfo.id === service.pricingInfo.id,
                  )
                ) {
                  acc[attendee.id][productInfo.id].services.push(service);
                }
              } else {
                acc[attendee.id][productInfo.id] = {
                  price: attendee.price.value,
                  services: [service],
                };
              }
            } else {
              acc[attendee.id] = {
                [productInfo.id]: {
                  price: attendee.price.value,
                  services: [service],
                },
              };
            }
          }
        });
      });

      return acc;
    }, {});

    return Object.entries(attendeesWithServiceRented)
      .reduce((reservedServicesByAttendee, [id, rentedServices]) => {
        const childIndex = attendeesWithInfos.children.findIndex(attendee => attendee.id === id);

        if (childIndex > -1) {
          return [
            ...reservedServicesByAttendee,
            {
              ...attendeesWithInfos.children[childIndex],
              id,
              index: childIndex + 1,
              isAdult: false,
              // $FlowFixMe 🤷‍️
              rentedServices: Object.values(rentedServices),
            },
          ];
        }

        const adultIndex = attendeesWithInfos.adults.findIndex(attendee => attendee.id === id);

        return [
          ...reservedServicesByAttendee,
          {
            ...attendeesWithInfos.adults[adultIndex],
            id,
            index: adultIndex + 1,
            isAdult: true,
            // $FlowFixMe 🤷‍️
            rentedServices: Object.values(rentedServices),
          },
        ];
      }, [])
      .sort((a, b) => {
        if (a.isAdult && !b.isAdult) {
          return -1;
        }

        if (!a.isAdult && b.isAdult) {
          return 1;
        }

        return a.index - b.index;
      });
  },
);

const getDetailedAttendees = ({ booking }: { booking: Booking }): BookingAttendeesType =>
  booking.attendees;

export {
  filterProposalBySamePackage,
  getAdditionalTransportsError,
  getIsLoadingAdditionalAccommodations,
  getIsLoadingAdditionalTransports,
  getProposals,
  getAdditionalAccommodations,
  getAdditionalAccommodationsFilters,
  getAdditionalAccommodationsError,
  getFilteredAdditionalAccommodations,
  getFilteredAdditionalAccommodationsSets,
  getAdditionalTransports,
  getAdditionalTransportsFilters,
  getFilteredAdditionalTransports,
  getAdditionalTransportsSorters,
  getReservedServicesByAttendee,
  getSelectedProposalId,
  getCurrentResort,
  getPaymentProviders,
  getProposalsWithMappedAttendees,
  getCurrentProposal,
  getProposalHasAnyEquipmentForRent,
  getDetailedAttendees,
  getAttendeesWithInfos,
  getConnectedUserInfoSelector,
  proposalsSelector,
  getSuggestions,
};
